import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import store from '@cp/src/store';
import {
  callCompleted,
  callRequested,
  resetCall,
  setError,
  setRecordingUrl,
  setStep,
  tabShareGranted
} from '@cp/src/reducers/whatsappCallSlice';
import {
  beginRecording,
  getMedia,
  hasActiveDisplayStream,
  releaseAll,
  releaseAudioOnly,
  resetMediaState,
  sendExtensionMessage,
  setupAudioMix,
  stopRecording
} from './mediaManager';
import { WA_MESSAGING_TYPES, WACallStates } from './interface';
import { clearStoredState, readStoredState } from './callStateStorage';
import { getTabId, isBlockingStatus } from './utils';
import { uploadRecording } from './actions';

let initialized = false;

const dispatch = (action: any) => store.dispatch(action);

const recoverFocus = (hideTab = false): void => {
  if (hideTab) sendExtensionMessage(WA_MESSAGING_TYPES.HIDE_TAB);
  window.focus();
  [50, 150, 300, 600].forEach(ms =>
    setTimeout(() => {
      if (!document.hasFocus()) window.focus();
    }, ms)
  );
};

const reestablishAudio = async (): Promise<void> => {
  const whatsappCallState = store.getState().whatsappCall;
  dispatch(setStep(WACallStates.SETTING_UP));
  addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_TabShareReused, {
    phone: whatsappCallState.pendingPhone,
    customerId: whatsappCallState.callCustomerId
  });
  try {
    await setupAudioMix(getMedia().displayStream!, () => {
      dispatch(setStep(WACallStates.NEEDS_TAB_SHARE));
    });
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_AudioSetup, {
      phone: whatsappCallState.pendingPhone,
      customerId: whatsappCallState.callCustomerId
    });
    sendExtensionMessage(WA_MESSAGING_TYPES.START_CALL, {
      phoneNumber: whatsappCallState.pendingPhone
    });
    sendExtensionMessage(WA_MESSAGING_TYPES.HIDE_TAB);
    recoverFocus();
  } catch (err: any) {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_AudioSetupFailed, {
      error: err?.message
    });
    toast.error(err?.message || 'Failed to set up audio. Please try again.');
    dispatch(setStep(WACallStates.NEEDS_TAB_SHARE));
  }
};

const handleRecordingComplete = (blob: Blob): void => {
  const whatsappCallState = store.getState().whatsappCall;
  if (!whatsappCallState.callLoanId || !whatsappCallState.callCustomerId) return;
  dispatch(setRecordingUrl({ url: URL.createObjectURL(blob), mimeType: blob.type || null }));
  dispatch(
    uploadRecording({
      blob,
      phoneNumber: whatsappCallState.pendingPhone,
      loanId: whatsappCallState.callLoanId,
      customerId: whatsappCallState.callCustomerId
    })
  );
};

const onMessage = (event: MessageEvent): void => {
  const msg = event.data;
  if (!msg?.type) return;
  // Skip our own outgoing extension messages echoed by window.postMessage.
  if (msg._lhMsgId && getMedia().sentMsgIds.delete(msg._lhMsgId)) return;

  // Non-owner browser tabs receive call state via the storage event in useCrossTabSync — they should not process extension messages here.
  const stored = readStoredState();
  if (stored && stored.ownerTabId !== getTabId() && isBlockingStatus(stored.status)) return;

  const whatsappCallState = store.getState().whatsappCall;

  switch (msg.type) {
    case WA_MESSAGING_TYPES.LOGIN_STATUS: {
      if (msg.data?.isLoggedIn) {
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_LoginSuccess, {
          phone: whatsappCallState.pendingPhone,
          customerId: whatsappCallState.callCustomerId
        });
        if (hasActiveDisplayStream()) {
          reestablishAudio();
        } else {
          dispatch(setStep(WACallStates.NEEDS_TAB_SHARE));
          addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_TabSharePrompt, {
            phone: whatsappCallState.pendingPhone,
            customerId: whatsappCallState.callCustomerId
          });
        }
      } else {
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_LoginFailed);
        dispatch(setError('Please log in to WhatsApp Web to make a call'));
        //focus whastapp tab
        sendExtensionMessage(WA_MESSAGING_TYPES.FOCUS_WHATSAPP_TAB);
      }
      break;
    }

    case WA_MESSAGING_TYPES.ON_INITIATED: {
      dispatch(setStep(WACallStates.IN_PROGRESS));
      beginRecording(handleRecordingComplete);
      sendExtensionMessage(WA_MESSAGING_TYPES.HIDE_TAB);
      recoverFocus();
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_Initiated, {
        phone: whatsappCallState.pendingPhone
      });
      break;
    }

    case WA_MESSAGING_TYPES.ON_ACCEPTED: {
      dispatch(setStep(WACallStates.CONNECTED));
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_Connected, {
        phone: whatsappCallState.pendingPhone,
        customerId: whatsappCallState.callCustomerId
      });
      break;
    }

    case WA_MESSAGING_TYPES.ON_DISCONNECTED: {
      const media = getMedia();
      const recorder = media.recorder;
      const hasActiveRecording = recorder && recorder.state === 'recording';
      const hasExistingBlob = !hasActiveRecording && !!media.recordingBlob;

      if (hasActiveRecording) {
        dispatch(callCompleted());
        // onstop → handleRecordingComplete → uploadRecording.
        stopRecording();
        releaseAudioOnly();
      } else if (hasExistingBlob) {
        dispatch(callCompleted());
        dispatch(
          setRecordingUrl({
            url: URL.createObjectURL(media.recordingBlob!),
            mimeType: media.recordingBlob!.type || null
          })
        );
        dispatch(
          uploadRecording({
            blob: media.recordingBlob!,
            phoneNumber: whatsappCallState.pendingPhone,
            loanId: whatsappCallState.callLoanId,
            customerId: whatsappCallState.callCustomerId
          })
        );
        releaseAudioOnly();
      } else {
        dispatch(resetCall());
        releaseAll();
        resetMediaState();
      }

      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_Disconnected, {
        phone: whatsappCallState.pendingPhone,
        customerId: whatsappCallState.callCustomerId
      });
      break;
    }

    case WA_MESSAGING_TYPES.ON_NUMBER_NOT_FOUND: {
      stopRecording(true);
      dispatch(setError('This number does not exist on WhatsApp'));
      releaseAudioOnly();
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_NumberNotFound, {
        phone: whatsappCallState.pendingPhone,
        customerId: whatsappCallState.callCustomerId
      });
      break;
    }

    case WA_MESSAGING_TYPES.ON_RATING_OVERLAY: {
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_RatingOverlay, {
        phone: whatsappCallState.pendingPhone,
        customerId: whatsappCallState.callCustomerId
      });
      sendExtensionMessage(WA_MESSAGING_TYPES.DISMISS_OVERLAY);
      break;
    }

    case WA_MESSAGING_TYPES.ON_ERROR: {
      // Ignore errors when no call is in flight
      if (!isBlockingStatus(whatsappCallState.status)) break;
      const errorMessage = msg.data?.errorMessage || 'An error occurred with WhatsApp';
      const isNumberNotFound = /number.*(not|does).*(exist|found)|unable to find voice/i.test(
        errorMessage
      );
      if (isNumberNotFound) {
        stopRecording(true);
        dispatch(setError('This number does not exist on WhatsApp'));
        releaseAudioOnly();
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_NumberNotFound, {
          phone: whatsappCallState.pendingPhone,
          rawError: errorMessage,
          customerId: whatsappCallState.callCustomerId
        });
      } else {
        dispatch(setError(errorMessage));
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_Error, {
          phone: whatsappCallState.pendingPhone,
          error: errorMessage
        });
      }
      break;
    }
  }
};

export const initCallCoordinator = (): void => {
  if (initialized) return;
  initialized = true;

  const whatsappCallState = store.getState().whatsappCall;
  if (
    isBlockingStatus(whatsappCallState.status) &&
    !getMedia().recorder &&
    !getMedia().displayStream
  ) {
    dispatch(resetCall());
  }

  window.addEventListener('message', onMessage);

  window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
    const stored = readStoredState();
    if (stored?.ownerTabId === getTabId() && isBlockingStatus(stored.status)) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  window.addEventListener('pagehide', (e: PageTransitionEvent) => {
    if (e.persisted) return;
    const stored = readStoredState();
    if (stored?.ownerTabId === getTabId() && isBlockingStatus(stored.status)) {
      handleOwnerTabUnloading();
      clearStoredState();
    }
  });
};

export const startCall = (phone: string, loanId: string, customerId: string): void => {
  dispatch(callRequested({ phone, loanId, customerId, ownerTabId: getTabId() }));
  addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_CallCTA, { phone, customerId });
  addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_LoginCheck, { phone, customerId });
  sendExtensionMessage(WA_MESSAGING_TYPES.GET_LOGIN_STATUS);
};

export const grantTabShare = async (): Promise<void> => {
  const whatsappCallState = store.getState().whatsappCall;
  dispatch(setStep(WACallStates.SETTING_UP));
  addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_AudioSetup, {
    phone: whatsappCallState.pendingPhone,
    customerId: whatsappCallState.callCustomerId
  });

  try {
    const CaptureCtrl = (window as any).CaptureController;
    const controller = CaptureCtrl ? new CaptureCtrl() : null;

    const constraints: DisplayMediaStreamOptions & Record<string, unknown> = {
      video: true,
      audio: true
    };
    if (controller) constraints.controller = controller;

    const ds = await navigator.mediaDevices.getDisplayMedia(constraints);

    // CaptureController.setFocusBehavior must be called within ~1 s of getDisplayMedia resolving — this prevents Chrome from switching focus to the captured tab.
    if (controller) {
      try {
        (controller as any).setFocusBehavior('no-focus-change');
      } catch (_) {
        /* not supported — fall back to manual focus */
      }
    }

    recoverFocus();

    const videoTrack = ds.getVideoTracks()[0];
    if (!videoTrack) {
      ds.getTracks().forEach(t => t.stop());
      toast.error('No video track found. Please try again.');
      dispatch(setStep(WACallStates.NEEDS_TAB_SHARE));
      recoverFocus(true);
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_TabShareDenied, {
        reason: 'no_video_track',
        customerId: whatsappCallState.callCustomerId
      });
      return;
    }

    const settings = videoTrack.getSettings() as MediaTrackSettings & { displaySurface?: string };
    const handle = await (videoTrack as any).getCaptureHandle?.();

    if (settings?.displaySurface !== 'browser' || handle?.handle !== 'WHATSAPP_TAB') {
      ds.getTracks().forEach(t => t.stop());
      toast.error('Please share the WhatsApp tab, not a window or full screen.');
      dispatch(setStep(WACallStates.NEEDS_TAB_SHARE));
      recoverFocus(true);
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_TabShareDenied, {
        reason: 'wrong_surface',
        surface: settings?.displaySurface,
        customerId: whatsappCallState.callCustomerId
      });
      return;
    }

    videoTrack.stop();

    const audioTracks = ds.getAudioTracks();
    if (!audioTracks.length) {
      ds.getTracks().forEach(t => t.stop());
      toast.error('No audio captured. Enable "Share audio" when selecting the WhatsApp tab.');
      dispatch(setStep(WACallStates.NEEDS_TAB_SHARE));
      recoverFocus(true);
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_NoAudioShared, {
        phone: whatsappCallState.pendingPhone
      });
      return;
    }

    getMedia().displayStream = ds;
    await setupAudioMix(ds, () => {
      dispatch(setStep(WACallStates.NEEDS_TAB_SHARE));
    });

    dispatch(tabShareGranted());
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_TabShareGranted, {
      phone: whatsappCallState.pendingPhone,
      customerId: whatsappCallState.callCustomerId
    });

    sendExtensionMessage(WA_MESSAGING_TYPES.START_CALL, {
      phoneNumber: whatsappCallState.pendingPhone,
      customerId: whatsappCallState.callCustomerId
    });
    recoverFocus(true);
  } catch (err: any) {
    recoverFocus(true);
    toast.error(err?.message || 'Failed to set up tab share. Please try again.');
    dispatch(setStep(WACallStates.NEEDS_TAB_SHARE));
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_AudioSetupFailed, {
      error: err?.message,
      customerId: whatsappCallState.callCustomerId
    });
  }
};

export const disconnectCall = (): void => {
  sendExtensionMessage(WA_MESSAGING_TYPES.DISCONNECT_CALL);
};

// Explicit user reset (from "Cancel" button, telephone change, call-type change away).
// Releases audio and recording state but leaves the displayStream intact so the user can retry without re-prompting the share dialog.
export const cancelAndReset = (
  reason: 'manual_reset' | 'telephone_change' | 'call_type_change'
): void => {
  const whatsappCallState = store.getState().whatsappCall;
  dispatch(resetCall());
  releaseAudioOnly();
  resetMediaState();
  addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_Reset, {
    reason,
    customerId: whatsappCallState.callCustomerId
  });
};

// Called when the owner browser tab is being unloaded (page close/refresh).
export const handleOwnerTabUnloading = (): void => {
  const stored = readStoredState();
  if (stored?.ownerTabId === getTabId() && isBlockingStatus(stored.status)) {
    sendExtensionMessage(WA_MESSAGING_TYPES.DISCONNECT_CALL);
    localStorage.setItem('WA_PENDING_DISCONNECT', stored.ownerTabId);
  }
};

// Called on hook init: complete a pending disconnect if a previous load triggered one before we could fully tear down (page navigated/refreshed mid-call).
export const completePendingDisconnect = (): void => {
  const pending = localStorage.getItem('WA_PENDING_DISCONNECT');
  if (pending && pending === getTabId()) {
    localStorage.removeItem('WA_PENDING_DISCONNECT');
    sendExtensionMessage(WA_MESSAGING_TYPES.DISCONNECT_CALL);
  }
};
